import React, { useRef, useState, useCallback } from 'react';
import { Stack, Tab, Tabs, Dialog, DialogTitle, DialogContent, Button, DialogActions } from '@mui/material';
import { TabPanel, tabProps } from '../../components/BasicTabs';
import UserInfoTab from './UserInfoTab';
import { LoadingButton } from '@mui/lab';
import { Rights, User, Roles } from '../../graphql/types';
import AccessTab from './AccessTab';
import useAuth from '../../hooks/useAuth';
import { CloseCircleOutlined, MailOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import PermissionsTab, { PermissionsTabRef } from './PermissionsTab';
import SecurityTab from './SecurityTab';
import BankInfoTabContent, { BankInfoTabContentHandle } from 'sections/banks/BankInfoTabContent';

export interface Props {
  entity?: any;
  onGridRefresh?: () => void;
  handleAdd: () => void;
  open?: boolean;
  onClose?: () => void;
  company?: any;
  onSave?: (user: User) => void;
  title?: string;
  role?: Roles[];
}

enum AccessTypeEnum {
  To = 'to',
  From = 'from'
}

const AddUser = ({ onGridRefresh, handleAdd, entity, open, onClose, onSave, company, title = 'User', role }: Props) => {
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.rights.includes(Rights.IsSuperAdmin);
  const [currentTab, setCurrentTab] = useState(0);
  const userInfoTabRef = useRef<any>();
  const bankInfoTabRef = useRef<BankInfoTabContentHandle>(null);
  const permissionsTabRef = useRef<PermissionsTabRef>(null);
  const [inviteOnSave, setInviteOnSave] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<Roles[]>(
    entity?.role ? (Array.isArray(entity.role) ? entity.role : [entity.role]) : []
  );
  const [initialRoles, setInitialRoles] = useState<Roles[]>(entity?.role ? (Array.isArray(entity.role) ? entity.role : [entity.role]) : []);

  React.useEffect(() => {
    if (entity?.role) {
      const roles = Array.isArray(entity.role) ? entity.role : [entity.role];
      setSelectedRoles(roles);
      setInitialRoles(roles);
    } else {
      setSelectedRoles([]);
      setInitialRoles([]);
    }
  }, [entity]);

  const handleRolesChange = useCallback(
    (roles: Roles[]) => {
      setSelectedRoles(roles);

      const rolesChanged = roles.length !== initialRoles.length || !roles.every((role) => initialRoles.includes(role));

      if (rolesChanged && permissionsTabRef.current) {
        permissionsTabRef.current.resetToDefaults();
      }
    },
    [initialRoles]
  );

  const getPermissionsData = useCallback(() => {
    if (!isSuperAdmin || !permissionsTabRef.current) {
      return { rights: [], hasCustomPermissions: false };
    }

    const hasCustomPermissions = permissionsTabRef.current.hasCustomPermissions();

    if (hasCustomPermissions) {
      return {
        rights: permissionsTabRef.current.getPermissions(),
        hasCustomPermissions: true
      };
    }

    return { rights: [], hasCustomPermissions: false };
  }, [isSuperAdmin]);

  const handleSave = () => {
    setInviteOnSave(false);
    if (userInfoTabRef.current) {
      userInfoTabRef.current.submitForm();
    }
  };
  const handleSaveAndInviteUser = () => {
    setInviteOnSave(true);
    if (userInfoTabRef.current) {
      userInfoTabRef.current.submitForm();
    }
  };

  const tabData = [
    {
      label: 'User Info',
      component: (
        <UserInfoTab
          ref={userInfoTabRef}
          entity={entity}
          onCancel={handleAdd}
          onSave={onSave || (() => {})}
          company={company}
          role={role}
          inviteOnSave={inviteOnSave}
          onRolesChange={handleRolesChange}
          getPermissionsData={getPermissionsData}
          onUserSaved={async (u) => {
            await bankInfoTabRef.current?.persistDrafts(u.id!);
          }}
        />
      )
    }
  ];

  if (entity) {
    tabData.push(
      {
        label: 'Access To',
        component: <AccessTab accessType={AccessTypeEnum.To} entity={entity} />
      },
      {
        label: 'Access From',
        component: <AccessTab accessType={AccessTypeEnum.From} entity={entity} />
      },
      {
        label: 'Security',
        component: <SecurityTab entity={entity} onRefresh={onGridRefresh} />
      }
    );
  }

  let bankInfoTabIndex = -1;
  let permissionsTabIndex = -1;

  bankInfoTabIndex = tabData.length;
  tabData.push({
    label: 'Bank Info',
    component: <BankInfoTabContent ref={bankInfoTabRef} variant="user" user={entity} />
  });

  if (isSuperAdmin) {
    permissionsTabIndex = tabData.length;
    tabData.push({
      label: 'Permissions',
      component: <PermissionsTab ref={permissionsTabRef} entity={entity} roles={selectedRoles} onRolesChange={handleRolesChange} />
    });
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <>
      <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>{entity ? `Edit ${title}` : `New ${title}`}</DialogTitle>
        <DialogContent>
          {tabData.length > 1 ? (
            <>
              <Tabs value={currentTab} onChange={handleTabChange}>
                {tabData.map((tab, index) => (
                  <Tab key={index} label={tab.label} {...tabProps(index)} />
                ))}
              </Tabs>
              {tabData.map((tab, index) => (
                <TabPanel key={index} value={currentTab} index={index} keepMounted={true}>
                  {tab.component}
                </TabPanel>
              ))}
            </>
          ) : (
            <UserInfoTab
              ref={userInfoTabRef}
              entity={entity}
              onCancel={handleAdd}
              onSave={onSave || (() => {})}
              company={company}
              role={role}
              inviteOnSave={inviteOnSave}
              onUserSaved={async (u) => {
                await bankInfoTabRef.current?.persistDrafts(u.id!);
              }}
            />
          )}
        </DialogContent>

        <DialogActions>
          <Stack direction="row" spacing={2} alignItems="center">
            <Button color="error" onClick={onClose} startIcon={<CloseCircleOutlined />}>
              Cancel
            </Button>
            {(currentTab === 0 || currentTab === bankInfoTabIndex || currentTab === permissionsTabIndex) && (
              <>
                {!entity && (
                  <LoadingButton
                    type="submit"
                    startIcon={<MailOutlined />}
                    variant="contained"
                    disabled={userInfoTabRef.current?.isEmailChecking()}
                    loading={userInfoTabRef.current?.isEmailChecking()}
                    onClick={() => handleSaveAndInviteUser()}
                  >
                    {`Add & Invite`}
                  </LoadingButton>
                )}
                <LoadingButton
                  type="submit"
                  startIcon={entity ? <SaveOutlined /> : <PlusOutlined />}
                  variant="contained"
                  disabled={userInfoTabRef.current?.isEmailChecking()}
                  loading={userInfoTabRef.current?.isEmailChecking()}
                  onClick={() => handleSave()}
                >
                  {entity ? `Save` : `Add`}
                </LoadingButton>
              </>
            )}
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default AddUser;
